import type { WorkplaceReminderDeepLink, WorkplaceReminderViewModel } from "@/lib/messages/workplaceReminderModel";
import { WorkplaceReminderHygieneActions, type ReassignMemberOption } from "./WorkplaceReminderHygieneActions";

const TONE_CLASS: Record<WorkplaceReminderViewModel["tone"], string> = {
  blue: "border-blue-200/90 bg-blue-50/90 text-blue-950 dark:border-blue-800/80 dark:bg-blue-950/40 dark:text-blue-50",
  amber: "border-amber-300/80 bg-amber-50/80 text-amber-950 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100",
  indigo: "border-indigo-200/80 bg-indigo-50/70 text-indigo-950 dark:border-indigo-800 dark:bg-indigo-950/35 dark:text-indigo-100",
  neutral: "border-gray-200 bg-gray-50/90 text-gray-900 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-gray-100",
};

export type WorkplaceReminderCardProps = {
  reminder: WorkplaceReminderViewModel;
  compact?: boolean;
  canReassign?: boolean;
  reassignMembers?: ReassignMemberOption[];
  onDismiss?: (notificationId: string) => void;
  onAcknowledge?: (notificationId: string) => void;
  onResolve?: (notificationId: string, reason?: string) => void;
  onReassign?: (notificationId: string, assigneeUserId: string, reason?: string) => void;
  onNavigate?: (link: WorkplaceReminderDeepLink) => void;
};

export function WorkplaceReminderCard({
  reminder,
  compact,
  canReassign,
  reassignMembers,
  onDismiss,
  onAcknowledge,
  onResolve,
  onReassign,
  onNavigate,
}: WorkplaceReminderCardProps) {
  const canNavigate = Boolean(onNavigate && hasNavigateTarget(reminder.deepLink));
  const canDismiss =
    reminder.dismissible &&
    reminder.deepLink.notification_id &&
    onDismiss &&
    reminder.source !== "hygiene_hint" &&
    reminder.source !== "org_nudge";

  const hygieneActions =
    reminder.hygieneActions &&
    reminder.source === "lead_hygiene" &&
    reminder.deepLink.notification_id;

  return (
    <div className={`rounded-lg border px-3 py-2.5 ${compact ? "text-[11px]" : "text-xs"} ${TONE_CLASS[reminder.tone]}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 font-semibold">{reminder.title}</div>
        {hygieneActions ? (
          <WorkplaceReminderHygieneActions
            notificationId={reminder.deepLink.notification_id!}
            deepLink={reminder.deepLink}
            canReassign={!!canReassign}
            reassignMembers={reassignMembers}
            onDismiss={canDismiss ? onDismiss : undefined}
            onResolve={onResolve}
            onReassign={onReassign}
            onNavigate={onNavigate}
          />
        ) : (
          <div className="flex shrink-0 gap-2">
            {canNavigate ? (
              <button
                type="button"
                className="text-[10px] font-medium underline opacity-80 hover:opacity-100"
                onClick={() => onNavigate!(reminder.deepLink)}
              >
                Open
              </button>
            ) : null}
            {canDismiss ? (
              <button
                type="button"
                className="text-[10px] font-medium underline opacity-80 hover:opacity-100"
                onClick={() => onDismiss!(reminder.deepLink.notification_id!)}
              >
                Dismiss
              </button>
            ) : null}
          </div>
        )}
      </div>
      {reminder.body ? (
        <p className={`mt-1 whitespace-pre-wrap opacity-95 ${compact ? "line-clamp-3" : ""}`}>{reminder.body}</p>
      ) : null}
      {reminder.requiresAck && reminder.status === "open" ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <p className="text-[10px] font-medium text-amber-800 dark:text-amber-200">
            Requires acknowledgement
          </p>
          {reminder.deepLink.notification_id && onAcknowledge ? (
            <button
              type="button"
              className="rounded-md bg-amber-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-amber-700"
              onClick={() => onAcknowledge(reminder.deepLink.notification_id!)}
            >
              Acknowledge
            </button>
          ) : null}
        </div>
      ) : null}
      {reminder.dueAt ? (
        <p className="mt-1 text-[10px] opacity-80">Due {new Date(reminder.dueAt).toLocaleString()}</p>
      ) : null}
      {reminder.footer ? <p className="mt-1.5 text-[10px] opacity-75">{reminder.footer}</p> : null}
    </div>
  );
}

function hasNavigateTarget(link: WorkplaceReminderDeepLink): boolean {
  return Boolean(link.lead_id || link.message_thread_id || link.checklist_item_id || link.notification_id);
}
